import { filter } from "rxjs";
import { booleanAttribute, Directive, ElementRef, inject, input, InputSignalWithTransform, Renderer2 } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

/**
 * enums
 */
import { CheckType, ICheckType } from "@sharedModules/forms/components/check/enums/check-type";

/**
 * models
 */
import { FormFieldControlBase } from '../form-field-control/form-field-control-model';

/**
 * @directive FormCheckFieldModel
 * @description
 * **Specialized model for checkbox and radio button form fields** in the SADIN Angular application.
 * 
 * This abstract base class extends `FormFieldControlBase` to provide specialized functionality
 * for checkbox and radio button controls. It handles boolean value management, checked state
 * tracking, multiple selection for checkboxes, and single selection for radio buttons.
 * The class manages the complex interaction between HTML input elements and Angular's form control system.
 * 
 * **Key Features:**
 * - ☑️ Checkbox and radio button support
 * - 🔄 Boolean value management
 * - 📋 Multiple selection for checkboxes
 * - 🎯 Single selection for radio buttons
 * - ✅ Checked state tracking
 * - 🔗 Form control integration
 * 
 * **Inheritance Chain:**
 * ```
 * FormFieldControlBase<boolean | unknown | unknown[] | null>
 * └── FormCheckFieldModel
 *     └── FormCheckFieldHighOrderDirectiveBase
 *         └── CheckFieldComponent
 * ```
 *
 * @extends FormFieldControlBase<boolean | unknown | unknown[] | null>
 *
 * @example
 * // Basic checkbox implementation
 * export class CustomCheckbox extends FormCheckFieldModel {
 *   public type: CheckType | string = CheckType.Checkbox;
 *   public checked: boolean = false;
 *   public inputElementRef: ElementRef<HTMLInputElement> = inject(ElementRef);
 *   public value: boolean = false;
 *   public id: string = 'custom-checkbox';
 *   public name: string = 'custom-checkbox';
 *   public readonly: boolean = false;
 *   public required: boolean = false;
 *   public disabled: boolean = false;
 * }
 *
 * @example
 * // Radio button implementation
 * export class CustomRadio extends FormCheckFieldModel {
 *   public type: CheckType | string = CheckType.Radio;
 *   public checked: boolean = false;
 *   public inputElementRef: ElementRef<HTMLInputElement> = inject(ElementRef);
 *   public value: boolean = false;
 *   public id: string = 'custom-radio';
 *   public name: string = 'custom-radio';
 *   public readonly: boolean = false;
 *   public required: boolean = false;
 *   public disabled: boolean = false;
 * }
 */
@Directive()
export abstract class FormCheckFieldModel extends FormFieldControlBase<boolean | unknown | unknown[] | null> {

    /**
     * @property renderer
     * @description Renderer2 instance for the check field
     * 
     * Used to set the checked state of the input element.
     * 
     * @private
     * @internal
     */
    public readonly renderer: Renderer2 = inject(Renderer2);

    /**
     * @property type
     * @description Type of the check field (checkbox or radio)
     * 
     * Determines whether this is a checkbox or radio button field.
     * Controls the behavior and value handling of the field.
     * 
     * @abstract
     * @required
     * @example
     * public type: ICheckType = CheckType.Checkbox;  // Checkbox
     * public type: ICheckType = CheckType.Radio;     // Radio button
     */
    public abstract type: ICheckType;

    /**
     * @property checked
     * @description Whether the field is currently checked
     * 
     * Represents the current checked state of the field.
     * For checkboxes, this can be true/false; for radio buttons, this indicates selection.
     * 
     * @abstract
     * @required
     * @example
     * public checked: boolean = false;  // Unchecked
     * public checked: boolean = true;   // Checked
     */
    public abstract checked: boolean

    /**
     * @property inputElementRef
     * @description Reference to the HTML input element
     * 
     * Required for accessing the native input element and managing the checked state.
     * This reference is used to trigger clicks, access properties, and manage the input.
     * 
     * @abstract
     * @required
     * @example
     * public inputElementRef: ElementRef<HTMLInputElement> = inject(ElementRef);
     */
    public abstract inputElementRef: ElementRef<HTMLInputElement>;

    /**
     * @property _innerValue
     * @description Internal value storage for complex scenarios
     * 
     * Stores the internal value for complex scenarios like multiple checkbox selections
     * or radio button groups. Used for advanced value management.
     * 
     * @private
     * @internal
     */
    public _innerValue: boolean | unknown | unknown[] | null = null

    /**
     * @input uiDisabled
     * @description Blocks user interaction with the native input **without** touching the
     * bound `FormControl`.
     *
     * Use it for options that must stay rendered and part of an enabled form but are not
     * selectable yet (e.g. "coming soon" placeholders).
     *
     * **Why not `disabled`?** On an element that also carries `formControlName`, the
     * `disabled` input name is owned by Angular's reactive forms layer:
     * - `FormControlName` declares its own `disabled` input purely to log the
     *   *"It looks like you're using the disabled attribute with a reactive form directive"* warning.
     * - `setDisabledState()` writes {@link FormFieldControlBase.disabled} from the control's own
     *   state during `setUpControl`, which runs after input binding — so a bound
     *   `[disabled]="true"` is immediately overwritten with `false` for an enabled control.
     *
     * `uiDisabled` is never written by `setDisabledState()`, so it survives every
     * `enable()` / `disable()` cycle and emits no warning.
     *
     * @default false
     * @example
     * ```html
     * <app-custom-option
     *   formControlName="schedule_type"
     *   [uiDisabled]="true"
     *   ... />
     * ```
     */
    public readonly uiDisabled: InputSignalWithTransform<boolean, unknown> = input(false, {
        transform: booleanAttribute
    });

    /**
     * @property isInputDisabled
     * @description Effective disabled state of the native input — true when either the
     * control-driven {@link FormFieldControlBase.disabled} flag or the UI-only
     * {@link uiDisabled} input is set.
     *
     * Bind this instead of `disabled` on the rendered `<input>`.
     *
     * @readonly
     * @example
     * ```html
     * <input type="radio" [disabled]="isInputDisabled" />
     * ```
     */
    public get isInputDisabled(): boolean {
        return this.disabled || this.uiDisabled();
    }

    /**
     * @property isChecked
     * @description Computed checked state based on value and type
     * 
     * Determines the actual checked state by considering the current value,
     * field type, and form control state. Handles both single and multiple selection scenarios.
     * 
     * @readonly
     * @example
     * // In template
     * <input type="checkbox" [checked]="isChecked">
     * 
     * // In component
     * if (this.isChecked) {
     *   console.log('Field is checked');
     * }
     */
    public get isChecked(): boolean {

        let result: boolean;
        
        if (!this.isCheckBox) {
            // Radio button logic - compare form control value with this component's value
            // The form control value represents the selected radio button's value
            result = this._innerValue === this.value;
        } else {
            // Checkbox logic
            if (Array.isArray(this._innerValue)) {
                // Array scenario
                result = this._innerValue.indexOf(this.value) !== -1;
            } else if (this.value != null) {
                // Single value scenario
                result = this._innerValue === this.value;
            } else {
                // Boolean scenario
                result = this._innerValue === true;
            }
        }
        
        return result;
    }

    /**
     * @property isCheckBox
     * @description Whether this field is a checkbox
     * 
     * Returns true if the field type is checkbox, false if it's a radio button.
     * Used for conditional logic and behavior differences.
     * 
     * @readonly
     * @example
     * // In template
     * <div [class.checkbox]="isCheckBox" [class.radio]="!isCheckBox">
     * 
     * // In component
     * if (this.isCheckBox) {
     *   // Checkbox-specific logic
     * } else {
     *   // Radio button-specific logic
     * }
     */
    public get isCheckBox(): boolean {
        return this.type === CheckType.Checkbox
    }

    /**
     * @property inputElement
     * @description Direct access to the native HTML input element
     * 
     * Provides direct access to the underlying HTML input element.
     * Useful for advanced operations and browser API access.
     * 
     * @readonly
     * @example
     * // Access the native input element
     * const input = this.inputElement;
     * input.click(); // Programmatically trigger click
     * 
     * // Check if element is disabled
     * if (input.disabled) {
     *   console.log('Input is disabled');
     * }
     */
    public get inputElement(): HTMLInputElement {
        return this.inputElementRef?.nativeElement
    }

    /**
     * @method _handlerInnerValueByType
     * @description Handles internal value updates based on field type
     * 
     * Manages the internal value storage based on whether this is a checkbox or radio button.
     * For checkboxes, handles multiple selection arrays; for radio buttons, handles single values.
     * 
     * @param checked Whether the field should be checked
     * @private
     * @example
     * // Internal method called when value changes
     * this._handlerInnerValueByType(true);  // Check the field
     * this._handlerInnerValueByType(false); // Uncheck the field
     */
    private _handlerInnerValueByType(checked: boolean): void {
        
        if (!this.isCheckBox) {
            // Radio button logic - when a radio is selected, set the form control value to this radio's value
            this._innerValue = this.value;
        } else {
            // Checkbox logic
            if (Array.isArray(this._innerValue)) {
                // Array scenario
                const index = this._innerValue.indexOf(this.value);
                if (checked && index === -1) {
                    this._innerValue.push(this.value);
                } else if (!checked && index !== -1) {
                    this._innerValue.splice(index, 1);
                }
            } else if (this.value != null) {
                // Single value scenario
                this._innerValue = checked ? this.value : null;
            } else {
                // Boolean scenario
                this._innerValue = checked;
            }
        }
    }

    /**
     * @method syncCheckedStateToElement
     * @description Syncs the native input checked property from {@link isChecked}.
     *
     * Attribute-only bindings do not update the live DOM property after user interaction;
     * imperative sync keeps one-way `[checked]` consumers (tables, ng-select, tree) aligned.
     *
     * @protected
     */
    protected syncCheckedStateToElement(): void {
        const el = this.inputElementRef?.nativeElement;
        if (!el) return;
        this.renderer.setProperty(el, 'checked', this.isChecked);
    }

    /**
     * @method writeValue
     * @description Override to handle boolean value updates
     * 
     * Updates the internal value and triggers appropriate change handling.
     * Ensures the form control and UI stay synchronized.
     * 
     * @param value The boolean value to set
     * @override
     * @example
     * // This method is called by Angular forms
     * // Override if you need custom value handling
     * public override writeValue(value: boolean): void {
     *   super.writeValue(value);
     *   // Additional custom logic
     * }
     */
    public override writeValue(value: unknown): void {

        // if the field is a checkbox and the value is an array, copy the value to avoid reference issues
        if (this.isCheckBox && Array.isArray(value)) this._innerValue = [...value];
        // otherwise, set the inner value to the value
        else this._innerValue = value;

        this.syncCheckedStateToElement();
    }

    /**
     * @method onInputChange
     * @description Handles input change events
     * 
     * Processes checkbox/radio change events, updates the internal value,
     * and notifies the form control of changes.
     * 
     * @param event The input change event
     * @example
     * // In template
     * <input type="checkbox" (change)="onInputChange($event)">
     * <input type="radio" (change)="onInputChange($event)">
     * 
     * // This method handles:
     * // - Value updates
     * // - Internal state management
     * // - Form control notifications
     * // - Event emission
     */
    public onInputChange(event: Event): void {
        
        // ES6 Destructuring
        // get the checked state from the event target
        const { checked } = event.target as HTMLInputElement;
        
        // handle the inner value by type
        this._handlerInnerValueByType(checked);
        
        // if the control is not null, send the value to the control
        if (this.control != null) {
            // get the value to send
            let valueToSend = this._innerValue;
            
            // if the field is a checkbox and the inner value is an array, create a copy to avoid reference issues
            if (this.isCheckBox && Array.isArray(this._innerValue)) {
                valueToSend = [...this._innerValue];
            }
            
            // send the value to the control
            this.onChange(valueToSend);
        }
        
        // emit the value to the component
        this.onChangeEvent.emit(this._innerValue);
    }

    /**
     * @method onInputTouched
     * @description Handles input touch events
     * 
     * Marks the field as touched when the user interacts with the input.
     * Used for validation state management and user feedback.
     * 
     * @example
     * // In template
     * <input type="checkbox" (blur)="onInputTouched()">
     * <input type="radio" (blur)="onInputTouched()">
     */
    public onInputTouched(): void {
        if (this.control != null) {
            this.onTouched();
        }
    }
    
    /**
     * @method onControlInit
     * @description Handles control initialization
     * 
     * Subscribes to the control's value changes and updates the internal value accordingly.
     * 
     * @override 
     */
    public override onControlInit(): void {
        this.control
            ?.valueChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(v => v != this._innerValue),
            )
            .subscribe(value => {
                this._innerValue = value;
                this.syncCheckedStateToElement();
            });
    }

}